import { useState, type FormEvent } from "react";
import { ApiError, createRefund, type Refund } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { formatAmount } from "@/lib/format";
import { AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";

type Props = {
  open: boolean;
  onClose: () => void;
  payment: {
    id: string;
    amount_minor: number;
    currency: string;
    livemode?: boolean;
    external_reference?: string | null;
  };
  alreadyRefunded?: number;
  onSuccess?: (refund: Refund) => void;
};

// Cap test mode (mirror du backend): 1 USD (100 minor) / 100 CDF
function testCap(currency: string): number {
  if (currency === "USD" || currency === "EUR") return 100;
  return 100;
}

export function RefundModal({ open, onClose, payment, alreadyRefunded = 0, onSuccess }: Props) {
  const remaining = payment.amount_minor - alreadyRefunded;
  const isLivemode = payment.livemode !== false; // default true if unknown
  const cap = isLivemode ? remaining : Math.min(remaining, testCap(payment.currency));

  const [amount, setAmount] = useState<string>(String(remaining));
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Refund | null>(null);

  const reset = () => {
    setAmount(String(remaining));
    setReason("");
    setLoading(false);
    setError(null);
    setDone(null);
  };

  const handleClose = () => {
    if (loading) return;
    if (done && onSuccess) onSuccess(done);
    reset();
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const n = parseInt(amount, 10);
      if (Number.isNaN(n) || n <= 0) throw new Error("Montant invalide");
      if (n > cap) throw new Error(`Montant supérieur au plafond (${formatAmount(cap, payment.currency)})`);
      const refund = await createRefund(payment.id, {
        amount: n,
        reason: reason.trim() || undefined,
      });
      setDone(refund);
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else if (err instanceof Error) setError(err.message);
      else setError("Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        title="Remboursement initié"
        size="md"
        closeOnOverlay={false}
        footer={
          <button onClick={handleClose} className="btn btn-primary btn-sm">
            Terminer
          </button>
        }
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-success-50 border-l-4 border-success-500 px-4 py-3 flex items-start gap-3">
            <CheckCircle2 size={20} className="text-success-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-success-700 text-sm">Refund {done.id}</div>
              <div className="text-xs text-success-700 mt-0.5">
                Status: <span className="font-mono">{done.status}</span> · Montant{" "}
                {formatAmount(done.amount_minor, done.currency)}
              </div>
            </div>
          </div>
          <div className="text-sm text-[#7e8299] leading-relaxed">
            Le remboursement est en cours auprès du switch. Le statut final apparaîtra dans la liste
            des refunds une fois le retour switch traité (typiquement quelques secondes).
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Rembourser le paiement"
      subtitle={payment.external_reference ? `Réf. ${payment.external_reference}` : payment.id}
      size="md"
      closeOnOverlay={!loading}
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={loading} className="btn btn-light btn-sm">
            Annuler
          </button>
          <button type="submit" form="refund-form" disabled={loading} className="btn btn-danger btn-sm">
            <RotateCcw size={14} />
            {loading ? "Traitement…" : "Confirmer le remboursement"}
          </button>
        </>
      }
    >
      <form id="refund-form" onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Info label="Montant initial" value={formatAmount(payment.amount_minor, payment.currency)} />
          <Info label="Déjà remboursé" value={formatAmount(alreadyRefunded, payment.currency)} />
          <Info label="Remboursable max" value={formatAmount(remaining, payment.currency)} highlight />
          <Info label="Devise" value={payment.currency} />
        </div>

        {!isLivemode && (
          <div className="rounded-lg bg-amber-50 border-l-4 border-amber-500 px-4 py-3 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <div className="font-bold">Mode test actif</div>
              Le plafond appliqué est <span className="font-mono">{formatAmount(testCap(payment.currency), payment.currency)}</span> ({payment.currency === "USD" ? "1 USD" : "100 CDF"}) — cap appliqué automatiquement par le backend.
            </div>
          </div>
        )}

        <div>
          <label className="form-label">Montant à rembourser ({payment.currency})</label>
          <input
            type="number"
            min={1}
            max={cap}
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="form-input"
          />
          <div className="form-hint">
            Entrez le montant en unité mineure (centimes pour USD, unité pour CDF). Maximum: {formatAmount(cap, payment.currency)}.
          </div>
        </div>

        <div>
          <label className="form-label">Motif (optionnel)</label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="form-input"
            placeholder="Ex: annulation du vol, double débit, demande client…"
            maxLength={500}
          />
        </div>

        {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      </form>
    </Modal>
  );
}

function Info({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-brand-50" : "bg-[#f5f8fa]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-1">{label}</div>
      <div className={`text-sm font-bold ${highlight ? "text-brand-700" : "text-[#181c32]"}`}>{value}</div>
    </div>
  );
}
